/**
 * From the `visitor` surface's wire shapes to the types the players read.
 *
 * The wire is snake_case and says what the server knows; the player types are camelCase and carry
 * a few fields no route answers — an image's or a video's dimensions, whether a video has sound, a
 * document's page count — which are set to the value a player already reads as "unknown" rather
 * than invented. A word on the wire the player has no member for — an asset type with no player,
 * an overlay it cannot render — drops that entry rather than reaching a component that would
 * throw on it.
 */

import type {
  AdsConfig,
  CollectionSummary,
  EmbedAssetSummary,
  EmbedItemEntry,
  EmbedView,
  OverlayEntry,
  PlayerConfig,
  PlayerThemeView,
} from '@/api/visitor/embeds'
import type {
  BatchPlaybackInfoResponse,
  ImageVariant as ImageVariantOut,
  LinkedFile as LinkedFileOut,
  PlaybackInfoView,
  SignedUrl as SignedUrlOut,
  SubtitleTrack as SubtitleTrackOut,
  VideoChapter as VideoChapterOut,
} from '@/api/visitor/playback'

import type {
  AssetListItem,
  AudioChapter,
  BasePlaybackInfo,
  ImageVariant,
  ImageVariantName,
  LinkedFile,
  PlaybackInfo,
  PublicMetadata,
  SubtitleTrack,
  VideoChapter,
} from './asset'
import { AssetType, createTimestamp, createUUID, type SignedUrl } from './base'
import type {
  Embed,
  EmbedAdsConfig,
  EmbedCollection,
  EmbedItem,
  EmbedPlayerConfig,
  OverlayAnimation,
  OverlayConfig,
  OverlayPosition,
  OverlayTriggerType,
  OverlayType,
  PlayerTheme,
} from './embed'
import type { CollectionSubtype } from './analytics'

// =============================================================================
// VOCABULARIES
// =============================================================================

/** The wire's words for a union's members, so a word the player lacks reads `undefined`. */
function vocabulary<T extends string>(members: readonly T[]): ReadonlyMap<string, T> {
  return new Map(members.map((member) => [member, member]))
}

/** `mixed` is a subtype no player mode renders as one kind; such a collection plays item by item. */
const COLLECTION_SUBTYPES = vocabulary<CollectionSubtype>(['videos', 'audios', 'images', 'documents'])
const THEMES = vocabulary<'dark' | 'light' | 'colorful'>(['dark', 'light', 'colorful'])
/** The five corners `embed_presets.logo_position` allows; a sixth word draws nothing. */
const LOGO_POSITIONS = vocabulary<NonNullable<PlayerTheme['logoPosition']>>([
  'top_left',
  'top_right',
  'bottom_left',
  'bottom_right',
  'control_bar',
])
const IMAGE_VARIANTS = vocabulary<ImageVariantName>(['thumbnail', 'medium', 'large', 'original'])
const OVERLAY_TYPES = vocabulary<OverlayType>(['cta', 'rating', 'info', 'custom_html', 'recommendations'])
const OVERLAY_TRIGGERS = vocabulary<OverlayTriggerType>(['time', 'percent', 'video_start', 'video_end', 'pause'])
const OVERLAY_POSITIONS = vocabulary<OverlayPosition>([
  'center',
  'top_left',
  'top_right',
  'top_center',
  'bottom_left',
  'bottom_right',
  'bottom_center',
  'fullscreen',
])
const OVERLAY_ANIMATIONS = vocabulary<OverlayAnimation>(['fade', 'slide_up', 'slide_down', 'scale', 'none'])

/** A collection's subtype as the player's collection modes know it, or `undefined` for `mixed`. */
export function toCollectionSubtype(word: string): CollectionSubtype | undefined {
  return COLLECTION_SUBTYPES.get(word)
}

// =============================================================================
// BASE TRANSFORMS
// =============================================================================

export function transformSignedUrl(data: SignedUrlOut): SignedUrl {
  return { url: data.url, expiresAt: createTimestamp(data.expires_at) }
}

/** Where a required URL is absent: an empty address, which the players read as nothing to load. */
function noUrl(expiresAt: string): SignedUrl {
  return { url: '', expiresAt: createTimestamp(expiresAt) }
}

function signedOrUndefined(data: SignedUrlOut | null | undefined): SignedUrl | undefined {
  return data ? transformSignedUrl(data) : undefined
}

// =============================================================================
// ASSET LIST ITEMS
// =============================================================================

/** What every list item is built from, whichever surface described the asset. */
export interface AssetListFields {
  uid: string
  name: string
  type: string
  description?: string
  thumbnailUrl?: string
  /** Seconds, for a timed medium. */
  duration?: number
  /** The custom fields marked public, displayed: what the details panel lists under the description. */
  publicMetadata?: PublicMetadata[]
}

/** One asset as a player lists it, or `undefined` for a type no player renders. */
export function toAssetListItem(fields: AssetListFields): AssetListItem | undefined {
  const base = {
    uid: createUUID(fields.uid),
    name: fields.name,
    description: fields.description,
    thumbnailUrl: fields.thumbnailUrl,
    publicMetadata: fields.publicMetadata ?? [],
  }
  switch (fields.type) {
    case AssetType.VIDEO:
      return {
        ...base,
        type: AssetType.VIDEO,
        duration: fields.duration ?? 0,
        dimensions: { width: 0, height: 0 },
        hasAudio: true,
      }
    case AssetType.AUDIO:
      return { ...base, type: AssetType.AUDIO, duration: fields.duration ?? 0 }
    case AssetType.IMAGE:
      return { ...base, type: AssetType.IMAGE, dimensions: { width: 0, height: 0 } }
    case AssetType.DOCUMENT:
      return { ...base, type: AssetType.DOCUMENT, pageCount: 0, isPreviewable: true }
    case AssetType.THIRD_PARTY:
      return { ...base, type: AssetType.THIRD_PARTY, duration: fields.duration }
    default:
      return undefined
  }
}

export function transformAssetListItem(data: EmbedAssetSummary): AssetListItem | undefined {
  return toAssetListItem({
    uid: data.uid,
    name: data.name,
    type: data.type,
    description: data.description ?? undefined,
    thumbnailUrl: data.thumbnail_url ?? undefined,
    duration: data.duration ?? undefined,
    publicMetadata: data.public_metadata.map((entry) => ({ label: entry.label, value: entry.value })),
  })
}

// =============================================================================
// PLAYBACK INFO
// =============================================================================

export function transformVideoChapter(data: VideoChapterOut): VideoChapter {
  return { title: data.title, startTime: data.start_time }
}

function transformAudioChapter(data: VideoChapterOut): AudioChapter {
  return { title: data.title, startTime: data.start_time }
}

export function transformLinkedFile(data: LinkedFileOut): LinkedFile {
  return {
    uid: createUUID(data.uid),
    name: data.name,
    type: data.type,
    fileUrl: signedOrUndefined(data.file_url),
  }
}

export function transformSubtitleTrack(data: SubtitleTrackOut): SubtitleTrack {
  return {
    uid: data.uid,
    label: data.label,
    language: data.language,
    isDefault: data.is_default,
    src: data.src,
  }
}

/** One image size, or none for a size name the player does not pick between. */
function transformImageVariant(data: ImageVariantOut): ImageVariant[] {
  const name = IMAGE_VARIANTS.get(data.name)
  if (!name) return []
  return [
    {
      name,
      dimensions: { width: data.dimensions.width, height: data.dimensions.height },
      url: transformSignedUrl(data.url),
    },
  ]
}

function basePlaybackInfo(data: PlaybackInfoView): Omit<BasePlaybackInfo, 'type'> {
  return {
    uid: createUUID(data.uid),
    expiresAt: createTimestamp(data.expires_at),
    sessionId: data.session_id ? createUUID(data.session_id) : null,
    resumePositionSeconds: data.resume_position_seconds ?? null,
    blocked: data.blocked,
  }
}

/**
 * One asset's playback info. **Throws for a type no player renders** — a folder, an archive —
 * which a player only asks about when an item it should not have listed reached it.
 */
export function transformPlaybackInfo(data: PlaybackInfoView): PlaybackInfo {
  const base = basePlaybackInfo(data)
  switch (data.type) {
    case AssetType.VIDEO:
      return {
        ...base,
        type: AssetType.VIDEO,
        hlsUrl: data.hls_url ? transformSignedUrl(data.hls_url) : noUrl(data.expires_at),
        sourceUrl: signedOrUndefined(data.source_url),
        chapters: data.chapters?.map(transformVideoChapter),
        trim: data.trim ? { start: data.trim.start, end: data.trim.end } : null,
        previewSpritesUrl: signedOrUndefined(data.preview_sprites_url),
        // Both branches carry it: a video with a sound track is owed the same drawing of the same
        // thing as an audio file, and the reader that wants it is the subtitle editor's timeline,
        // whose subject is usually a video.
        waveformUrl: signedOrUndefined(data.waveform_url),
        linkedFiles: data.linked_files?.map(transformLinkedFile),
      }
    case AssetType.AUDIO:
      return {
        ...base,
        type: AssetType.AUDIO,
        source: data.source ? transformSignedUrl(data.source) : noUrl(data.expires_at),
        waveformUrl: signedOrUndefined(data.waveform_url),
        chapters: data.chapters?.map(transformAudioChapter),
        subtitleTracks: data.subtitle_tracks?.map(transformSubtitleTrack),
        linkedFiles: data.linked_files?.map(transformLinkedFile),
      }
    case AssetType.IMAGE:
      return {
        ...base,
        type: AssetType.IMAGE,
        variants: (data.variants ?? []).flatMap(transformImageVariant),
      }
    case AssetType.DOCUMENT:
      return {
        ...base,
        type: AssetType.DOCUMENT,
        url: data.url ? transformSignedUrl(data.url) : noUrl(data.expires_at),
      }
    case AssetType.THIRD_PARTY:
      return {
        ...base,
        type: AssetType.THIRD_PARTY,
        embedUrl: data.embed_url ?? '',
        provider: data.provider ?? '',
        providerId: data.provider_id ?? '',
      }
    default:
      throw new Error(`Unknown playback info type: ${data.type}`)
  }
}

/** A batch's answers, keyed by asset uid. An asset the token does not reach is simply absent. */
export function transformBatchPlaybackInfo(data: BatchPlaybackInfoResponse): Map<string, PlaybackInfo> {
  const result = new Map<string, PlaybackInfo>()
  for (const [assetUid, rawInfo] of Object.entries(data.playback_infos)) {
    result.set(assetUid, transformPlaybackInfo(rawInfo))
  }
  return result
}

// =============================================================================
// EMBED
// =============================================================================

export function transformEmbedCollection(data: CollectionSummary): EmbedCollection | undefined {
  const subtype = toCollectionSubtype(data.subtype)
  return subtype ? { subtype, totalItems: data.total_items } : undefined
}

export function transformEmbedPlayerConfig(data: PlayerConfig): EmbedPlayerConfig {
  return {
    autoPlay: data.auto_play,
    loop: data.loop,
    shuffle: data.shuffle,
    theme: THEMES.get(data.theme),
    iconColor: data.icon_color ?? undefined,
    allowSourceDownload: data.allow_source_download,
    ecoModeDefault: data.eco_mode_default,
    subtitlesDefaultMode: data.subtitles_default_mode === 'auto_detect' ? 'auto_detect' : 'specific',
    subtitlesAutoStart: data.subtitles_auto_start,
  }
}

/**
 * The fields an overlay component reads, out of the typed columns the wire carries: the overlay
 * components still read one `payload` object per type, and only `custom_html` travels as one.
 */
function overlayPayload(type: OverlayType, data: OverlayEntry): Record<string, unknown> {
  switch (type) {
    case 'cta':
      return {
        text: data.cta_text ?? undefined,
        button_text: data.cta_button_text ?? undefined,
        button_url: data.cta_button_url ?? undefined,
        button_color: data.cta_button_color ?? undefined,
        button_variant: data.cta_button_variant ?? undefined,
      }
    case 'rating':
      return {
        question: data.rating_question ?? undefined,
        enable_comment: data.rating_allows_comment ?? false,
      }
    case 'info':
      return { title: data.info_title ?? undefined, message: data.info_message ?? undefined }
    case 'recommendations':
      // The videos are resolved by the server when the player opens and are each in the token's
      // `r`, so switching to one is a `playback/info` under the token this embed already holds.
      return {
        title: data.recommendations_title ?? undefined,
        items: data.recommended_assets.map((asset) => ({
          asset_uid: asset.uid,
          name: asset.name,
          thumbnail_url: asset.thumbnail_url ?? undefined,
          duration: asset.duration_ms == null ? undefined : asset.duration_ms / 1000,
        })),
      }
    default:
      return data.payload
  }
}

/** One overlay, or none when one of its words is not one the player renders. */
export function transformOverlay(data: OverlayEntry): OverlayConfig[] {
  const overlayType = OVERLAY_TYPES.get(data.overlay_type)
  const triggerType = OVERLAY_TRIGGERS.get(data.trigger_type)
  if (!overlayType || !triggerType) return []
  return [
    {
      uid: createUUID(data.uid),
      overlayType,
      payload: overlayPayload(overlayType, data),
      triggerType,
      triggerTime: data.trigger_time_ms == null ? undefined : data.trigger_time_ms / 1000,
      triggerPercent: data.trigger_percent ?? undefined,
      position: OVERLAY_POSITIONS.get(data.placement) ?? 'center',
      isDismissible: data.is_dismissible,
      autoHideSeconds: data.auto_hide_ms == null ? undefined : data.auto_hide_ms / 1000,
      animation: OVERLAY_ANIMATIONS.get(data.animation) ?? 'fade',
      showOnce: data.show_once,
      pausesVideo: data.pauses_video,
      showMarker: data.show_marker,
      priority: data.display_rank,
    },
  ]
}

export function transformAdsConfig(data: AdsConfig): EmbedAdsConfig {
  return {
    vastUrl: data.vast_url,
    // Milliseconds on the wire, the schema having one time unit; seconds here, which is what a
    // VAST `skipoffset` is in and therefore what the two are compared in.
    forceSkipAfter: data.skip_after_ms != null ? data.skip_after_ms / 1000 : undefined,
  }
}

/** One item of an embed, or none for an asset no player renders — a folder a share names. */
export function transformEmbedItem(data: EmbedItemEntry): EmbedItem[] {
  const asset = transformAssetListItem(data.asset)
  if (!asset) return []
  return [
    {
      asset,
      position: data.position,
      customTitle: data.custom_title ?? undefined,
      customDescription: data.custom_description ?? undefined,
      clip:
        data.start_time != null || data.end_time != null
          ? { start: data.start_time ?? 0, end: data.end_time ?? Infinity }
          : undefined,
      overlays: data.overlays.flatMap(transformOverlay),
    },
  ]
}

/**
 * The resolved skin, as the players read it.
 *
 * **It used to be dropped, and the reason it was is the reason it is read now.** Until
 * `docs/plans/embed-presets.md` P2 the field carried a share's `shares.metadata.theme` verbatim —
 * the previous platform's grouping — which is not the shape `PlayerTheme` describes, so this
 * function did not exist and the line that would have called it said so. The backend now answers
 * the flat shape and there is nothing left to translate but the case of the keys.
 *
 * A `logo_position` outside the five the player draws is dropped rather than passed on, which is
 * this file's rule for every vocabulary: a word a component would throw on does not reach it.
 */
export function transformPlayerTheme(data: PlayerThemeView): PlayerTheme {
  return {
    baseTheme: THEMES.get(data.base_theme) ?? 'dark',
    primaryColor: data.primary_color ?? undefined,
    accentColor: data.accent_color ?? undefined,
    controlsBgColor: data.controls_bg_color ?? undefined,
    controlsTextColor: data.controls_text_color ?? undefined,
    progressBarColor: data.progress_bar_color ?? undefined,
    logoUrl: data.logo_url ?? undefined,
    logoPosition: data.logo_position ? LOGO_POSITIONS.get(data.logo_position) : undefined,
    logoClickUrl: data.logo_click_url ?? undefined,
    showTitle: data.show_title,
    showBranding: data.show_branding,
    customCss: data.custom_css ?? undefined,
  }
}

/**
 * An embed as the players read it.
 */
export function transformEmbed(data: EmbedView): Embed {
  return {
    uid: createUUID(data.uid),
    embedType: data.embed_type === 'collection' ? 'collection' : 'single',
    name: data.name,
    description: data.description ?? undefined,
    thumbnailUrl: data.thumbnail_url ?? undefined,
    items: data.items.flatMap(transformEmbedItem),
    collection: data.collection ? transformEmbedCollection(data.collection) : undefined,
    playerConfig: transformEmbedPlayerConfig(data.player_config),
    playerTheme: transformPlayerTheme(data.player_theme),
    adsConfig: data.ads_config ? transformAdsConfig(data.ads_config) : undefined,
    collectionPlaybackId: data.collection_playback_id ? createUUID(data.collection_playback_id) : undefined,
    playbackToken: data.playback_token,
    viewerId: createUUID(data.viewer_id),
    sessionId: data.session_id ? createUUID(data.session_id) : undefined,
  }
}
